import React, { useEffect } from "react";
import {
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Heading,
  Icon,
  Button,
  Text,
} from "@chakra-ui/core";
import { useState } from "react";
import { NavbarWithState } from "components/shared/NavbarWithSearch";
import PageContainer from "components/containers/PageContainer";
import { useQuery } from "@apollo/react-hooks";
import { StoryList } from "components/shared/StoryList";
import { FooterWithState } from "components/Footer";
import gql from "graphql-tag";
import Link from "next/link";
import styled from "styled-components";
import { useRecordSearchResultSelected } from "hooks/analytics/useRecordSearchResultSelected";
import { ApiStory } from "data/api/ApiStory";

const searchStoriesQuery = gql`
  query searchStoriesQuery($searchString: String!, $maxStoriesShown: Int!) {
    searchStories(searchString: $searchString, first: $maxStoriesShown) {
      id
      slug
      publicationDate
      title
      summary
      featuredImage {
        url
      }
      author {
        id
        slug
        fullName
      }
    }

    numSearchStories(searchString: $searchString)
  }
`;

function useDebouncedSearchParameters(searchString: string) {
  const [maxStoriesShown, setMaxStoriesShown] = useState(10);
  const [debouncedSearchString, setDebouncedSearchString] =
    useState(searchString);

  function resetMaxStoriesShown() {
    setMaxStoriesShown(10);
  }

  useEffect(() => {
    // Set debouncedValue to value (passed in) after the specified delay
    const handler = setTimeout(() => {
      setDebouncedSearchString(searchString);
      resetMaxStoriesShown();
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [searchString]);
  return { maxStoriesShown, setMaxStoriesShown, debouncedSearchString };
}

function useSearchParameters() {
  const [rawSearchString, setRawSearchString] = useState("");
  const { maxStoriesShown, setMaxStoriesShown, debouncedSearchString } =
    useDebouncedSearchParameters(rawSearchString);

  return {
    debouncedSearchString,
    rawSearchString,
    setSearchString: setRawSearchString,
    maxStoriesShown,
    setMaxStoriesShown,
  };
}

function useSearchStories(searchString: string, maxStoriesShown: number) {
  const { networkStatus, loading, data } = useQuery(searchStoriesQuery, {
    variables: { searchString, maxStoriesShown },
  });

  const numStoriesMatchingSearchString = data?.numSearchStories;
  const stories = data?.searchStories ?? [];
  const loadingForTheFirstTime = networkStatus === 1;
  const updatingSearchResults = networkStatus === 2;

  return {
    stories,
    numStoriesMatchingSearchString,
    loading,
    loadingForTheFirstTime,
    updatingSearchResults,
  };
}

export function SearchPage({ errorPage }) {
  const {
    debouncedSearchString,
    rawSearchString,
    setSearchString,
    maxStoriesShown,
    setMaxStoriesShown,
  } = useSearchParameters();

  const recordSearchResultSelected = useRecordSearchResultSelected();

  useEffect(() => {
    recordSearchResultSelected({
      query: debouncedSearchString,
      article_clicked: false,
      article_title: "",
      article_id: "",
    });
  }, [debouncedSearchString]);

  const {
    stories,
    loading,
    numStoriesMatchingSearchString,
    updatingSearchResults,
  } = useSearchStories(debouncedSearchString, maxStoriesShown);

  const searchStringIsEmpty = debouncedSearchString.trim().length === 0;
  const resultsEmpty = stories.length === 0;
  const showNoResults = !updatingSearchResults && resultsEmpty;
  const showPlaceholderImage =
    searchStringIsEmpty || (updatingSearchResults && resultsEmpty);

  return (
    <PageContainer>
      <SearchBar
        searchResultsLoading={loading}
        searchString={rawSearchString}
        onChangeSearchString={(newString) => setSearchString(newString)}
        errorPage={errorPage}
      />
      {showPlaceholderImage ? (
        <SearchImage />
      ) : showNoResults ? (
        <NoResultsPlaceholder />
      ) : (
        <SearchResults
          stories={stories}
          onClickResult={(story) => {
            recordSearchResultSelected({
              query: debouncedSearchString,
              article_clicked: true,
              article_title: story?.title ?? "",
              article_id: story?.id ?? "",
            });
          }}
          onClickLoadMore={() => setMaxStoriesShown(maxStoriesShown + 10)}
          showLoadMoreButton={maxStoriesShown <= numStoriesMatchingSearchString}
        />
      )}
      <FooterWithState />
    </PageContainer>
  );
}

const HoverText = styled.text`
  color: #808080;
  fontsize: sm;
  fontfamily: Poppins;
  fontweight: normal;
  :hover {
    color: rgb(214, 74, 62);
    cursor: pointer;
  }
`;

export function NoPageLoaded() {
  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center">
      <Text
        textAlign="center"
        fontSize="xl"
        fontFamily="Poppins"
        fontWeight="bold"
      >
        Sorry, we couldn’t find that.
      </Text>
      <Text
        maxWidth="450px"
        textAlign="center"
        fontSize="sm"
        fontFamily="Poppins"
        fontWeight="normal"
        m={4}
      >
        Try searching for it or check out our latest reporting on our{" "}
        <Link href="/">
          <HoverText>home page</HoverText>
        </Link>
        .
      </Text>
    </Flex>
  );
}

export function SearchBar({
  searchResultsLoading,
  searchString,
  onChangeSearchString,
  errorPage,
}) {
  return (
    <Flex
      w="100vw"
      px={2}
      py={[2, 8]}
      alignItems="center"
      justifyContent="center"
      position="sticky"
      top={["56px", "64px"]}
      bg="gray.100"
      zIndex={30}
      flexDirection="column"
    >
      {errorPage ? <NoPageLoaded /> : null}
      <Flex width="300px" flexGrow={1} alignItems="center" m={2}>
        <InputGroup width="100%" size="lg">
          <InputLeftElement
            children={
              searchResultsLoading ? <Spinner /> : <Icon name="search" />
            }
          />
          <Input
            value={searchString}
            onChange={(e) => onChangeSearchString(e.target.value)}
            size="lg"
            width="100%"
            boxShadow="md"
            placeholder="Search our stories"
            _focus={{
              boxShadow: "md",
              borderWidth: 2,
              borderColor: "blue.300",
            }}
          />
        </InputGroup>
      </Flex>
    </Flex>
  );
}

export function SearchImage() {
  return (
    <Flex
      flexGrow={1}
      justifyContent="center"
      alignItems="flex-start"
      bg="gray.100"
      minH="100vh"
    >
      <Flex
        w="100%"
        height="300px"
        backgroundSize="contain"
        backgroundRepeat="no-repeat"
        backgroundPosition="center"
        backgroundImage="url(/static/hero_image.png)"
      />
    </Flex>
  );
}

export function SearchResultsContainer({ children }) {
  return (
    <Flex flexGrow={1} bg="gray.100">
      {children}
    </Flex>
  );
}

export function ResultsLoadingSpinner() {
  return (
    <Flex
      flexGrow={1}
      bg="gray.100"
      justifyContent="center"
      alignItems="center"
    >
      <Spinner />
    </Flex>
  );
}

export function SearchResults({
  stories,
  onClickLoadMore,
  showLoadMoreButton,
  onClickResult,
}: {
  stories: ApiStory[];
  onClickLoadMore: () => void;
  showLoadMoreButton: boolean;
  onClickResult?: (story: ApiStory) => void;
}) {
  return (
    <Flex flexGrow={1} bg="gray.100" flexDirection="column" alignItems="center">
      <StoryList onClickStory={onClickResult} stories={stories} />
      {showLoadMoreButton && (
        <Flex flexGrow={1} pb={10} px={4} justifyContent="center">
          <Button
            size="lg"
            w={["100%", "auto"]}
            color="white"
            bg="red.400"
            _hover={{ bg: "red.700", color: "white" }}
            _active={{ bg: "red.700", color: "white" }}
            onClick={onClickLoadMore}
          >
            Load More
          </Button>
        </Flex>
      )}
    </Flex>
  );
}

export function NoResultsPlaceholder() {
  return (
    <Flex
      bg="gray.100"
      flexGrow={1}
      justifyContent="center"
      alignItems="flex-start"
      minH="100vh"
    >
      <Heading color="gray.400" fontSize="2xl">
        No results
      </Heading>
    </Flex>
  );
}
